import { Injectable } from "@angular/core";

/**
 * Interface per la struttura di un singolo token salvato
 */
interface SignatureData {
  token: string;
  isCompleted: boolean;
}

/**
 * Service per la gestione del localStorage relativo alle firme digitali
 * Gestisce il salvataggio e il recupero dello stato di completamento delle firme
 * Supporta multipli token salvati contemporaneamente
 */
@Injectable({
  providedIn: "root"
})
export class SignatureStorageService {
  
  /** Chiave utilizzata per salvare i dati nel localStorage */
  private readonly STORAGE_KEY = 'signature_tokens'; // Rinominato al plurale per chiarezza

  constructor() { }

  /**
   * Recupera tutti i dati delle firme dal localStorage
   * @returns Array dei dati delle firme salvate o array vuoto se non presenti
   */
  private getAllSignatureData(): SignatureData[] {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      
      if (!data) {
        return [];
      }
      
      return JSON.parse(data) as SignatureData[];
    } catch (error) {
      console.error('Errore durante il recupero dei dati delle firme:', error);
      return [];
    }
  }

  /**
   * Salva l'array completo dei dati nel localStorage
   * @param data - Array di SignatureData da salvare
   */
  private saveAllSignatureData(data: SignatureData[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Errore durante il salvataggio dei dati delle firme:', error);
    }
  }

  /**
   * Aggiunge o aggiorna i dati di una firma nel localStorage
   * Se il token esiste già, aggiorna il suo stato, altrimenti lo aggiunge
   * @param token - Token della firma
   * @param isCompleted - Stato di completamento della firma
   */
  saveSignatureData(token: string, isCompleted: boolean): void {
    try {
      const allData = this.getAllSignatureData();
      
      // Cerca se il token esiste già
      const existingIndex = allData.findIndex(item => item.token === token);
      
      if (existingIndex !== -1) {
        // Token esistente, aggiorna lo stato
        allData[existingIndex].isCompleted = isCompleted;
      } else {
        // Nuovo token, aggiungilo all'array
        const newData: SignatureData = {
          token: token,
          isCompleted: isCompleted
        };
        allData.push(newData);
      }
      
      // Salva l'array aggiornato
      this.saveAllSignatureData(allData);
    } catch (error) {
      console.error('Errore durante il salvataggio dei dati della firma:', error);
    }
  }

  /**
   * Verifica se un token specifico è già stato firmato con successo
   * @param token - Token da verificare
   * @returns true se il token è già stato firmato, false altrimenti
   */
  isTokenAlreadySigned(token: string): boolean {
    try {
      const allData = this.getAllSignatureData();
      
      // Cerca il token specifico
      const signatureData = allData.find(item => item.token === token);
      
      // Verifica che esista e sia completato
      return signatureData !== undefined && signatureData.isCompleted === true;
    } catch (error) {
      console.error('Errore durante la verifica del token:', error);
      return false;
    }
  }

  /**
   * Recupera i dati di un token specifico
   * @param token - Token da cercare
   * @returns I dati del token o null se non trovato
   */
  getSignatureDataByToken(token: string): SignatureData | null {
    try {
      const allData = this.getAllSignatureData();
      return allData.find(item => item.token === token) || null;
    } catch (error) {
      console.error('Errore durante il recupero dei dati del token:', error);
      return null;
    }
  }

  /**
   * Recupera tutti i token firmati con successo
   * @returns Array di token completati
   */
  getCompletedTokens(): string[] {
    try {
      const allData = this.getAllSignatureData();
      return allData
        .filter(item => item.isCompleted === true)
        .map(item => item.token);
    } catch (error) {
      console.error('Errore durante il recupero dei token completati:', error);
      return [];
    }
  }

  /**
   * Rimuove un token specifico dal localStorage
   * @param token - Token da rimuovere
   */
  removeSignatureData(token: string): void {
    try {
      const allData = this.getAllSignatureData();
      const filteredData = allData.filter(item => item.token !== token);
      this.saveAllSignatureData(filteredData);
    } catch (error) {
      console.error('Errore durante la rimozione del token:', error);
    }
  }

  /**
   * Rimuove tutti i dati delle firme dal localStorage
   * Utile per reset completo o pulizia
   */
  clearAllSignatureData(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Errore durante la rimozione di tutti i dati delle firme:', error);
    }
  }

  /**
   * Verifica se esistono dati di firma salvati
   * @returns true se esistono dati salvati, false altrimenti
   */
  hasSignatureData(): boolean {
    return this.getAllSignatureData().length > 0;
  }

  /**
   * Conta quanti token sono salvati
   * @returns Il numero di token salvati
   */
  getTokenCount(): number {
    return this.getAllSignatureData().length;
  }

  /**
   * Conta quanti token sono stati completati
   * @returns Il numero di token completati
   */
  getCompletedTokenCount(): number {
    return this.getCompletedTokens().length;
  }
}
